import React from 'react';
import { Col, Row, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS as AGENT_OVERVIEW_NS } from './i18n';
import './AgentOverviewPage.css';
import './AgentOverviewSkeleton.css';

/**
 * Loading state for the agent Overview page. Mirrors the real layout —
 * breadcrumb, header and module grid — so the page doesn't jump once
 * `currentAssistant.agentType` resolves. Rendered whenever the selected
 * agent hasn't loaded yet, instead of falling back to the conversational
 * module set (see `modulesFor` in agent-modules.ts): a QA agent must never
 * flash conversational-only sections such as Conversational/Process Flows.
 */
export const AgentOverviewSkeleton: React.FC = () => {
  const { t } = useTranslation(AGENT_OVERVIEW_NS);
  return (
    <div className="agent-overview-wrapper" aria-busy aria-label={t('page.loadingAriaLabel')}>
      <div className="agent-overview__breadcrumb">
        <Skeleton.Input active size="small" style={{ width: 48, height: 14 }} />
      </div>

      <div className="agent-overview__body">
        <div className="agent-overview__content-card">
          <div className="agent-overview__header">
            <div className="agent-overview__header-flex">
              <Skeleton.Input active size="small" style={{ width: 64, height: 16, borderRadius: 4 }} />
              <Skeleton.Input
                active
                size="large"
                style={{ width: 220, height: 32, borderRadius: 8, marginTop: 8 }}
              />
            </div>
            {/* The real search's class, so it takes its widths (320 → 240 → 100%). */}
            <div className="agent-overview__search">
              <Skeleton.Input active block style={{ height: 40, borderRadius: 12 }} />
            </div>
          </div>

          {/* The page's own grid (1 · 2 · 3 columns), not an approximation of it. */}
          <Row gutter={[16, 16]} className="agent-overview__grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} xl={8}>
                <ModuleCardSkeleton />
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </div>
  );
};

function ModuleCardSkeleton() {
  return (
    <div className="agent-overview-skeleton__card" aria-hidden>
      <div className="agent-overview-skeleton__card-top">
        {/* Same box as .module-card__logo: 60px, radius md. */}
        <Skeleton.Avatar
          active
          size={60}
          shape="square"
          style={{ borderRadius: 'var(--silia-radius-md)' }}
        />
        <Skeleton.Button active size="small" style={{ width: 36, height: 36, borderRadius: 10 }} />
      </div>
      <div className="agent-overview-skeleton__card-bottom">
        <Skeleton.Input active style={{ width: '60%', height: 24, borderRadius: 6 }} />
        <Skeleton.Input active size="small" style={{ width: '90%', height: 16, borderRadius: 4 }} />
      </div>
    </div>
  );
}
